"use client";

import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { MARKETING_NAV, type MenuLink } from "@/components/marketing-nav/nav-data";
import { cn } from "@/lib/utils";
import { SectionHeading } from "../../home/section";
import { Stagger, StaggerItem } from "../../home/motion";

/** Platform feature rows, sourced from the nav so the two never drift. */
const PLATFORM_ENTRY = MARKETING_NAV.find(
  (entry) => entry.type === "mega" && entry.title === "Platform",
);
const FEATURES: MenuLink[] =
  PLATFORM_ENTRY && PLATFORM_ENTRY.type === "mega" ? PLATFORM_ENTRY.items : [];

function FeatureCell({ feature, active }: { feature: MenuLink; active: boolean }) {
  const Icon = feature.icon;
  return (
    <StaggerItem className="group relative bg-background">
      <Link
        href={feature.href}
        aria-current={active ? "page" : undefined}
        className="flex h-full min-h-[168px] flex-col px-6 py-7 transition-colors duration-200 hover:bg-muted/40 sm:px-8 sm:py-8"
      >
        {Icon ? (
          <Icon className="size-5 text-muted-foreground/70 transition-colors group-hover:text-foreground" />
        ) : null}

        <div className="mt-auto pt-8">
          <div className="flex items-baseline gap-1.5">
            <h3 className="text-base font-bold tracking-tight text-foreground">{feature.title}</h3>
            {active ? (
              <span className="text-[11px] font-medium text-primary dark:text-brand-accent">
                · You're here
              </span>
            ) : (
              <ArrowRight className="size-4 -translate-x-1 self-center text-muted-foreground opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
            )}
          </div>
          {feature.description ? (
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              {feature.description}
            </p>
          ) : null}
        </div>
      </Link>
    </StaggerItem>
  );
}

/**
 * Shared "explore the rest of the platform" grid for feature pages. Renders the
 * full platform feature list as a numbered grid and subtly marks the page the
 * visitor is currently on.
 */
export function PlatformGrid({ className }: { className?: string }) {
  const pathname = usePathname();

  return (
    <section className={cn("border-t border-border bg-background pt-20 lg:pt-28", className)}>
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeading
          align="center"
          size="sm"
          title="One platform for the entire customer journey."
          description="Every part of Helios works together, so your team runs on a single system from first hello to loyal member."
        />
      </div>

      {/* Framed band: hatched side gutters, bordered card grid (Attio-style). */}
      <div className="mt-12 border-t border-border bg-grid lg:mt-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Stagger className="grid grid-cols-1 gap-px border-x border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature) => (
              <FeatureCell key={feature.title} feature={feature} active={pathname === feature.href} />
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}
